import { useEffect, useRef, useState } from 'react';

import { useInitializeTranslations } from '@telialt/common-cms/src/useTranslations';

import { useStickyPageActions } from '../hooks/useStickyPageActions';

const ACTION_ID = 'scroll-to-top';

export const useRegisterScrollToTopAction = () => {
    const { t } = useInitializeTranslations();

    const { registerAction, toggleVisibleAction } = useStickyPageActions();

    const [windowHeight, setWindowHeight] = useState(0);
    const isVisibleRef = useRef(false);
    const isRegisteredRef = useRef(false);

    function getFirstFocusableElementInContent(): HTMLElement | null {
        const selector = 'a:not(#skip-to-main-content), button, [tabindex]:not([tabindex="-1"])';
        const focusableElements = document.querySelectorAll<HTMLElement>(selector);

        return focusableElements.length > 0 ? focusableElements[0] : null;
    }

    const clickHandler = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        getFirstFocusableElementInContent()?.focus();
    };

    useEffect(() => {
        if (!t || isRegisteredRef.current) {
            return;
        }
        isRegisteredRef.current = true;
        registerAction({
            id: ACTION_ID,
            index: 1,
            title: t['sticky_page_actions.scroll_to_top.title'],
            icon: 'arrow-up',
            onClick: clickHandler,
            isVisible: false,
        });
    }, [registerAction, t]);

    useEffect(() => {
        const handleResize = () => {
            setWindowHeight(window.innerHeight);
        };
        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const newIsVisible = window.scrollY > windowHeight;
            if (newIsVisible !== isVisibleRef.current) {
                toggleVisibleAction(ACTION_ID, newIsVisible);
                isVisibleRef.current = newIsVisible;
            }
        };
        handleScroll();
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [toggleVisibleAction, windowHeight]);
};
